/**
 * 組織レポート v3 モバイル版。
 *
 * 正典: apps/web/docs/新方針20260821/モック_GB組織レポート_v3 (3).html
 *
 * 【方針（2026-08-22）】
 *  スマホからの参照なので詳細な解説は置かず、各セクションの要点を順に見せる。
 *  算出・文言はPC版とまったく同じモジュールを使う（判定が1箇所なので、
 *  PCとモバイルで結論が食い違うことは起きない）。変えるのは見せ方だけ。
 *
 * 【PC版との差】
 *  ・表紙・目次・中扉は出さない（スクロールで読むため）
 *  ・「この項目の見方」など詳細な解説は省く
 *  ・3スコアの説明は最も低い1つだけ（02-RULE-1つ）
 *  ・つながりの構造は部署ごとのアコーディオン
 *  ・全社員のタイプ一覧は出さない
 */
import React, { useState } from 'react';
import type { AxisKey } from '../utils/gb74Report';
import type { OrgReportV3Data } from '../utils/buildOrgReportV3';
import {
  CONNECTION_VAR_LABEL,
  GRADE_LABEL,
  SCORE_LABEL,
  SCORE_SUBLABEL,
  type ScoreKey,
} from '../utils/orgScoreV3';
import {
  CONSULT_SHAPE_META,
  consultDeptSummary,
  type ConsultDeptResultV3,
  type ConsultShapeV3,
} from '../utils/orgConsultV3';
import { POLAR_MIN_MEMBERS } from '../utils/orgCohesionV3';
import { DONT_DO_BY_THEME } from '../utils/orgImprovementCopyV3';
import { OrgDiamondV3 } from './OrgDiamondV3';
import './Gb74OrgReportMobile.scss';

const BLUE = '#006ABD';
const AXIS_LABEL: Record<AxisKey, string> = { A: 'A 論理', B: 'B 着実', C: 'C 共感', D: 'D 創造' };
const AXES: AxisKey[] = ['A', 'B', 'C', 'D'];

/** 円を <path> で描く（<circle> はキャプチャ経路で上書きされ得るため）。 */
function circlePath(cx: number, cy: number, r: number): string {
  return `M ${cx - r},${cy} a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 ${-r * 2},0`;
}

/** 中心からの極座標をSVG座標に変換する。 */
function polar(deg: number, r: number, c: number): [number, number] {
  const rad = (deg * Math.PI) / 180;
  return [c + r * Math.cos(rad), c - r * Math.sin(rad)];
}

const DEG: Record<AxisKey, number> = { A: 135, D: 45, C: 315, B: 225 };
const DIA_ORDER: AxisKey[] = ['A', 'D', 'C', 'B'];

/** 社員の分布。各社員を本人の4軸で描くミニ・ダイヤで置く。 */
const Scatter: React.FC<{ points: OrgReportV3Data['scatter']['points'] }> = ({ points }) => {
  const W = 290;
  const c = W / 2;
  const R = c - 22;
  const px = (x: number) => c + (x / 24) * R;
  const py = (y: number) => c - (y / 24) * R;
  return (
    <svg
      viewBox={`0 0 ${W} ${W}`}
      style={{ display: 'block', width: '100%', maxWidth: 290, margin: '0 auto' }}
    >
      <rect x="0.5" y="0.5" width={W - 1} height={W - 1} rx="8" fill="#FAFBFC" stroke="#E7EAEE" />
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <path key={f} d={circlePath(c, c, R * f)} fill="none" stroke="#D8E4EF" />
      ))}
      <line x1="0" y1={c} x2={W} y2={c} stroke="#D8E4EF" />
      <line x1={c} y1="0" x2={c} y2={W} stroke="#D8E4EF" />
      <text x="10" y="16" fontSize="10.5" fontWeight="700" fill={BLUE}>
        {AXIS_LABEL.A}
      </text>
      <text x={W - 10} y="16" fontSize="10.5" fontWeight="700" fill={BLUE} textAnchor="end">
        {AXIS_LABEL.D}
      </text>
      <text x={W - 10} y={W - 8} fontSize="10.5" fontWeight="700" fill={BLUE} textAnchor="end">
        {AXIS_LABEL.C}
      </text>
      <text x="10" y={W - 8} fontSize="10.5" fontWeight="700" fill={BLUE}>
        {AXIS_LABEL.B}
      </text>
      {points.map((p, i) => {
        const x = px(p.x);
        const y = py(p.y);
        const pts = DIA_ORDER.map((k) => {
          const r = (Math.max(0, Math.min(12, p.axis[k])) / 12) * 9;
          return polar(DEG[k], r, 0)
            .map((n, j) => (j === 0 ? x + n : y + n))
            .map((n) => n.toFixed(1))
            .join(',');
        }).join(' ');
        return (
          <polygon
            key={i}
            points={pts}
            fill="rgba(0,106,189,0.35)"
            stroke={BLUE}
            strokeWidth="1.1"
            strokeLinejoin="round"
          />
        );
      })}
    </svg>
  );
};

/** 部署の相談ネットワーク図（アコーディオンの中）。 */
const ConsultNet: React.FC<{ stat: ConsultDeptResultV3 }> = ({ stat }) => {
  const W = 280;
  const c = W / 2;
  const R = c - 34;
  const color = stat.shape ? CONSULT_SHAPE_META[stat.shape].color : '#8A94A2';
  const tri = stat.shape === '助け合い' ? stat.triangleIds : null;
  const hub = stat.shape === '一極集中' || stat.shape === '集まりぎみ' ? stat.topId : null;
  const pos = new Map<string, { x: number; y: number }>();
  if (tri) {
    const r = R * 0.45;
    tri.forEach((id, i) => {
      const a = (2 * Math.PI * i) / 3 - Math.PI / 2;
      pos.set(id, { x: c + r * Math.cos(a), y: c + r * Math.sin(a) });
    });
    const rest = stat.members.filter((m) => !tri.includes(m.id));
    rest.forEach((m, i) => {
      const a = (2 * Math.PI * i) / Math.max(1, rest.length) - Math.PI / 2;
      pos.set(m.id, { x: c + R * Math.cos(a), y: c + R * Math.sin(a) });
    });
  } else {
    if (hub) pos.set(hub, { x: c, y: c });
    const rest = stat.members.filter((m) => m.id !== hub);
    rest.forEach((m, i) => {
      const a = (2 * Math.PI * i) / Math.max(1, rest.length) - Math.PI / 2;
      pos.set(m.id, { x: c + R * Math.cos(a), y: c + R * Math.sin(a) });
    });
  }
  const triKey = new Set(
    tri
      ? [
          [tri[0], tri[1]],
          [tri[1], tri[2]],
          [tri[0], tri[2]],
        ].map((p) => p.sort().join('|'))
      : []
  );
  return (
    <svg
      viewBox={`0 0 ${W} ${W}`}
      style={{ display: 'block', width: '100%', maxWidth: 280, margin: '4px auto 0' }}
    >
      {stat.edges.map((e, i) => {
        const a = pos.get(e.from);
        const b = pos.get(e.to);
        if (!a || !b) return null;
        const isTri = triKey.has([e.from, e.to].sort().join('|'));
        const toHub = hub != null && e.to === hub;
        return (
          <line
            key={i}
            x1={a.x.toFixed(1)}
            y1={a.y.toFixed(1)}
            x2={b.x.toFixed(1)}
            y2={b.y.toFixed(1)}
            stroke={isTri ? '#2E7D46' : toHub ? color : '#C9D2DC'}
            strokeWidth={isTri ? 3 : toHub ? 1.8 : 1}
            strokeOpacity={isTri ? 0.95 : toHub ? 0.85 : 0.5}
          />
        );
      })}
      {stat.members.map((m) => {
        const p = pos.get(m.id);
        if (!p) return null;
        const inTri = Boolean(tri?.includes(m.id));
        const isHub = m.id === hub;
        const r = isHub ? 18 : inTri ? 14 : 11;
        const fill = isHub ? color : inTri ? '#2E7D46' : '#FFFFFF';
        return (
          <g key={m.id}>
            <path
              d={circlePath(p.x, p.y, r)}
              fill={fill}
              stroke={isHub ? color : inTri ? '#2E7D46' : '#B7C3CF'}
              strokeWidth={isHub || inTri ? 1.8 : 1.2}
            />
            {(isHub || inTri) && (
              <text
                x={p.x}
                y={p.y + 4}
                textAnchor="middle"
                fontSize="11"
                fontWeight="700"
                fill="#FFF"
              >
                {stat.inDegree[m.id] ?? 0}
              </text>
            )}
            <text
              x={p.x}
              y={p.y + r + 11}
              textAnchor="middle"
              fontSize="9.5"
              fill="#17202E"
              stroke="#FFF"
              strokeWidth="2.5"
              paintOrder="stroke"
            >
              {m.name}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

/** 部署1件のアコーディオン。 */
const DeptRow: React.FC<{ stat: ConsultDeptResultV3 }> = ({ stat }) => {
  const [open, setOpen] = useState(false);
  const meta = stat.shape ? CONSULT_SHAPE_META[stat.shape] : null;
  const tone = meta ? meta.tone : 'gray';
  return (
    <div className="gb74om-dept">
      <button
        type="button"
        className="gb74om-dhd"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={`mk gb74om-tone-${tone}`}>{meta ? meta.mark : '—'}</span>
        <span className="nm">
          <b>{stat.dept}</b>
          <span>回答 {stat.respondents}名</span>
        </span>
        <span className="st" style={{ color: meta?.color }}>
          {meta ? meta.label : '判定なし'}
        </span>
        <span className="arw">▶</span>
      </button>
      {open && (
        <div className="gb74om-dbody">
          <ConsultNet stat={stat} />
          <p style={{ margin: '8px 0 0' }}>{consultDeptSummary(stat)}</p>
        </div>
      )}
    </div>
  );
};

const SCORE_KEYS: ScoreKey[] = ['wellbeing', 'connection', 'retention'];
const LEGEND_SHAPES: ConsultShapeV3[] = ['助け合い', '集まりぎみ', '一極集中', '希薄'];

export const Gb74OrgReportMobile: React.FC<{ data: OrgReportV3Data }> = ({ data }) => {
  const { scores, type, axisMeans, texts, scatter, cohesion, improvement, distribution } = data;
  const scoreOf = (k: ScoreKey) =>
    k === 'connection'
      ? scores.connection
      : k === 'wellbeing'
        ? scores.wellbeing
        : scores.retention;
  const weak = texts.score[scores.weakest];

  return (
    <div className="gb74om">
      <div className="gb74om-bar">
        <span className="lg">GB組織</span>
        <span>{data.companyName}</span>
      </div>

      <div className="gb74om-body">
        <div className="gb74om-hd1">GB組織 診断レポート</div>
        <div className="gb74om-hd2">
          {data.companyName}
          <span>
            {data.period}／{data.answered}名が回答
          </span>
        </div>

        {/* 01 組織の評価 */}
        <div className="gb74om-card">
          <div className="gb74om-score">
            <p className="lb">GB組織スコア</p>
            <p className="v">
              {scores.gbScore}
              <small>/100</small>
            </p>
            <div className="grade">
              {scores.grade} ─ {GRADE_LABEL[scores.grade]}
            </div>
            <p className="cap">3つの評価の平均。評価は最も低い1つで決まります</p>
          </div>
          <div className="gb74om-type">
            <p className="lb">GB組織タイプ</p>
            <p className="ty">{type.code}</p>
            {type.shape && <span className="chip">{type.shape}</span>}
            <OrgDiamondV3 axisMeans={axisMeans} />
            <p className="gb74om-sub" style={{ textAlign: 'left' }}>
              ※ 4軸の合計は24点で固定です。一人ひとりが尖っていても、組織平均は中央に寄ります。
            </p>
          </div>
        </div>
        {texts.catch?.見出し && <div className="gb74om-catch">{texts.catch.見出し}</div>}

        {/* 02 この評価が示していること */}
        <div className="gb74om-sec">
          <div className="gb74om-sn">02</div>
          <div className="gb74om-st">この評価が示していること</div>
          <div className="gb74om-sq">3つの点数と、タイプの意味</div>

          <div className="gb74om-card">
            {SCORE_KEYS.map((k) => {
              const s = scoreOf(k);
              const on = scores.weakest === k;
              return (
                <div className="gb74om-sb" key={k}>
                  <div className="n">
                    <b style={on ? { color: BLUE } : undefined}>{SCORE_LABEL[k]}</b>
                    <span>{SCORE_SUBLABEL[k]}</span>
                  </div>
                  <div className="r">
                    <div className="m">
                      <span className="g" style={{ color: on ? BLUE : '#9CC3E3' }}>
                        {s.grade}
                      </span>
                      <span className="v">{s.score}点</span>
                    </div>
                    <div className="t">
                      <div
                        className="f"
                        style={{ width: `${s.score}%`, background: on ? BLUE : '#9CC3E3' }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="gb74om-sub">
              A（80〜100）良好 ／ B（60〜79）注意 ／ C（40〜59）要対応 ／ D（0〜39）緊急
            </div>
          </div>

          {/* 説明は最も低い1つだけ（02-RULE-1つ） */}
          {weak && (
            <div className="gb74om-note _hi">
              <span className="lb">いま最も低いのは「{SCORE_LABEL[scores.weakest]}」です</span>
              {weak.本文}
              {scores.weakest === 'connection' && (
                <div className="gb74om-sub">
                  内訳：
                  {texts.connVars
                    .map((v) => `${CONNECTION_VAR_LABEL[v.key]} ${v.value.toFixed(2)}`)
                    .join(' ／ ')}
                </div>
              )}
            </div>
          )}

          <div className="gb74om-card">
            <div className="gb74om-sub" style={{ marginTop: 0, marginBottom: 8 }}>
              その持ち味が強く出ている社員の割合です
            </div>
            {[...AXES]
              .sort((a, b) => type.rates[b] - type.rates[a])
              .map((k) => (
                <div className="gb74om-rate" key={k}>
                  <span className="n">{AXIS_LABEL[k]}</span>
                  <span className="t">
                    <span className="f" style={{ width: `${type.rates[k] * 100}%` }} />
                  </span>
                  <span className="v">
                    {Math.round(type.rates[k] * 100)}%（
                    {Math.round(type.rates[k] * type.memberCount)}名）
                  </span>
                </div>
              ))}
            {texts.shape?.本文 && (
              <div className="gb74om-note _hi">
                <span className="lb">御社は{type.shape ?? '判定なし'}です</span>
                {texts.shape.本文}
              </div>
            )}
            <div className="gb74om-sub">
              ※ 割合の合計は100%になりません。2つの持ち味が同時に強い方がいるためです。
              <br />※ タイプに優劣はありません。
              {type.isChanceLevel && (
                <>
                  <br />※ 偶然の範囲と明確には区別できません。参考値としてご覧ください。
                </>
              )}
            </div>
          </div>
        </div>

        {/* 03 社員の分布 */}
        <div className="gb74om-sec">
          <div className="gb74om-sn">03</div>
          <div className="gb74om-st">社員の分布</div>
          <div className="gb74om-sq">一人ひとりの持ち味が、どこにあるか</div>
          <div className="gb74om-card">
            <Scatter points={scatter.points} />
            {distribution.text?.見出し && (
              <div className="gb74om-note _hi">
                <span className="lb">{distribution.text.見出し}</span>
                {distribution.text.本文}
              </div>
            )}
            <div className="gb74om-sub">
              ※ ひとつのひし形が社員1名です。とがっている方向が、その方の強く出ている持ち味です。
              <br />※ 位置にも形にも優劣はありません。お名前は表示していません。
            </div>
          </div>
        </div>

        {/* 04 つながりの構造（アコーディオン） */}
        <div className="gb74om-sec">
          <div className="gb74om-sn">04</div>
          <div className="gb74om-st">つながりの構造</div>
          <div className="gb74om-sq">部署ごとに、相談がどう流れているか</div>
          {data.consult.length === 0 ? (
            <div className="gb74om-note">
              相談相手の回答がまだありません。回答が集まると部署ごとの相談の流れを表示します。
            </div>
          ) : (
            <>
              <div className="gb74om-note _hi" style={{ marginTop: 0 }}>
                見るところは1つだけです。
                <b>相談が1人に集まっているか、互いに相談し合えているか。</b>
              </div>
              <div className="gb74om-leg">
                {LEGEND_SHAPES.map((k) => (
                  <span key={k}>
                    <i className={`gb74om-tone-${CONSULT_SHAPE_META[k].tone}`}>
                      {CONSULT_SHAPE_META[k].mark}
                    </i>
                    {CONSULT_SHAPE_META[k].label}
                  </span>
                ))}
              </div>
              {data.consult.map((d) => (
                <DeptRow key={d.dept} stat={d} />
              ))}
              <div className="gb74om-sub">
                ※ 部署名をタップすると、その部署の相談関係が開きます。
              </div>
            </>
          )}
        </div>

        {/* 06 評価を上げるために */}
        <div className="gb74om-sec">
          <div className="gb74om-sn">05</div>
          <div className="gb74om-st">組織評価を上げる</div>
          <div className="gb74om-sq">今月やること、ひとつだけ</div>

          {/* 今月やること。PC版 3-1 の左カラムと同じ並び */}
          <div className="gb74om-card">
            <p className="gb74om-action">{improvement.text?.取り組み}</p>
            {improvement.focusDept && improvement.theme === '集中解消' && (
              <p style={{ fontSize: 12, lineHeight: 1.85, margin: '0 0 8px' }}>
                起点は{improvement.focusDept.dept}です。回答{improvement.focusDept.respondents}名中
                {improvement.focusDept.maxInDegree}名が{improvement.focusDept.topName}
                さんを挙げています。
              </p>
            )}
            <p style={{ fontSize: 12, lineHeight: 1.85, margin: 0 }}>{improvement.text?.理由}</p>

            {improvement.howTo && (
              <>
                <p className="gb74om-h">具体的な進め方</p>
                <p style={{ fontSize: 12, lineHeight: 1.85, margin: 0 }}>{improvement.howTo}</p>
              </>
            )}

            <p className="gb74om-h">やってはいけないこと</p>
            <p className="gb74om-dont">{DONT_DO_BY_THEME[improvement.theme]}</p>

            <p className="gb74om-h">GB組織スコアへの期待値</p>
            <div className="gb74om-outlook">
              <span className="lb">現状</span>
              <span className="now">{improvement.gbOutlook.now}</span>
              <span className="ar">→</span>
              <span className="lb _b">期待値</span>
              <span className="tg">{improvement.gbOutlook.target}</span>
            </div>
            <div className="gb74om-sub">
              ※
              「最も低い項目が次の段階まで上がったら、他がそのままなら全体はどうなるか」を計算した目安です。実際の変化は状況により異なります。
            </div>
          </div>

          {/* 余力があったら。PC版 3-1 の右カラムと同じ並び */}
          <p className="gb74om-h2">もし、余力があったらあわせて考えたいこと</p>

          <div className="gb74om-card">
            <p className="gb74om-h3">GB組織タイプ／社員の分布から</p>
            {cohesion.text?.次の一手 && cohesion.text.次の一手 !== '—' && (
              <p style={{ fontSize: 12, lineHeight: 1.85, margin: 0 }}>{cohesion.text.次の一手}</p>
            )}
            {cohesion.text?.本文 && (
              <p style={{ fontSize: 12, lineHeight: 1.85, margin: '14px 0 0' }}>
                {cohesion.text.本文}
              </p>
            )}
            {cohesion.modifiers.map((m) => (
              <p
                key={m.id}
                style={{ fontSize: 12, lineHeight: 1.85, margin: '5px 0 0', color: '#3d4757' }}
              >
                {m.本文}
              </p>
            ))}
            {cohesion.polarHigh === null && (
              <div className="gb74om-sub">
                ※ 回答者が{POLAR_MIN_MEMBERS}名に満たないため、判定は行っていません。
              </div>
            )}
          </div>

          {data.archetypeActions.length > 0 && (
            <div className="gb74om-card">
              <p className="gb74om-h3">組織の持ち味から（どれか1つを試してみる）</p>
              {data.archetypeActions.map((a) => (
                <div className="gb74om-arche" key={a.title}>
                  <p className="t">{a.title}</p>
                  <p className="b">{a.body}</p>
                </div>
              ))}
            </div>
          )}

          <div className="gb74om-band">
            まずは出来ること1つをしっかりと90日続けてみることを推奨いたします
          </div>
        </div>

        <div className="gb74om-foot">GB組織 診断レポート｜GB照らす</div>
      </div>
    </div>
  );
};

export default Gb74OrgReportMobile;
